import * as React from 'react';
import { useCallback, useMemo } from 'react';

import EventIcon from '@mui/icons-material/Event';
import { Box } from '@mui/material';
import {
  DateRange,
  DateRangePicker,
  DateRangePickerProps,
} from '@mui/x-date-pickers-pro';

import { isValidDate } from '@sbv-clients/util-misc';

import { FormHelperText } from '../form/form';
import { FormControllerTitle } from '../style/form-controllers';

const transformer = {
  get: (dateRange: Date[]) => {
    const [start, end] = dateRange;
    return [
      start ? new Date(start) : null,
      end ? new Date(end) : null,
    ] as DateRange<Date>;
  },

  post: (value: DateRange<Date>) => {
    const [start, end] = value;
    const startValid = isValidDate(start);
    const endValid = isValidDate(end);
    if (!startValid && !endValid) return [];
    if (!startValid && endValid) return [null, end];
    if (startValid && !endValid) return [start, null];
    return [start, end] as Date[];
  },
};

export function DateRangePickerField({
  label,
  dateRange,
  setDateRange,
  required,
  minDate,
  maxDate,
}: {
  label?: string;
  dateRange: Date[];
  setDateRange: (dateRange: Date[]) => void;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
}) {
  const value = useMemo(() => transformer.get(dateRange), [dateRange]);

  const onChange = useCallback(
    (value: DateRange<Date> | null) => {
      if (!value) return;
      let [start, end] = value;
      start = start ?? end ?? new Date();
      end = end ?? start;
      setDateRange([start, end]);
    },
    [setDateRange]
  );

  return (
    <Box>
      <FormControllerTitle>{label}</FormControllerTitle>
      <DateRangePicker
        value={value}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        closeOnSelect={false}
        slots={{ openPickerIcon: EventIcon }}
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
          },
        }}
      />
      {required && (!value[0] || !value[1]) && (
        <FormHelperText>Date is required</FormHelperText>
      )}
    </Box>
  );
}

export type { DateRange, DateRangePickerProps };
export { DateRangePicker };
